import { getCookie } from '@visa/lib-cookie';
import CardEnums from '../../src/constants/card-brand-type-enums';
import {
  MESSAGE_DELIMITER,
  SRCI_REQUEST_RECEIVED,
  SYS_PUSH_GTM_DATA_EVENT,
  THM_PROFILING
} from '../../src/constants/gtm-events';
import { SRC_SYSTEM_IFRAME_ID } from '../../src/constants/iframe';
import { MPGS } from '../../src/constants/merchant';
import canUseSessionStorage from '../../src/helpers/can-use-session-storage';
import isEmail from '../../src/helpers/is-email';
import isEmptyObject from '../../src/helpers/is-empty-object';
import getIncomingTrafficSource from '../helpers/get-incoming-traffic-source';
import { cookieExist, getSsiStatus, getUserType } from './cookie-manager';

let gtmInstrumentationInstance = null;

const _gtmPush = data => {
  const srcSystemFrame = document.getElementById(SRC_SYSTEM_IFRAME_ID);
  if (srcSystemFrame && srcSystemFrame.contentWindow) {
    srcSystemFrame.contentWindow.postMessage(data, '*');
  }
};

export const srcInitiatorIdMap = {
  // VCO-14529: Mastercard Payment Gateway Services Limited apikey
  '6CB7SWU7FEU2MNQM6AIM21-4t0oWwhGCh1f8hfeN6DYg3FPLc': MPGS,
  // MA apikey
  JK0TVYHU43R1D9M2IU9G21BHE7Wxi584CeZQnZCffv4_o0ezc: CardEnums.CARD_BRANDS.MASTERCARD
};

export const trackINOEvent = (gtmEvent, data) => {
  const gtmData = {
    event: gtmEvent,
    event_action: gtmEvent,
    event_label: gtmEvent
  };

  if (data) {
    gtmData.correlation_id = data.correlation_id ? data.correlation_id : data.srciTransactionId;
    gtmData.incoming_traffic_source = data.incoming_traffic_source;
    gtmData.isnewuser = data.isnewuser;
    gtmData.is_external_src = data.is_external_src || false;
    // VCO-13757 when user starts journey from Mastercard to Visa, api_key is not available
    // Record api_key if passed in src_initiator_id before we set it to a card brand
    if (gtmEvent === SRCI_REQUEST_RECEIVED) {
      if (
        typeof data.src_initiator_id === 'string' &&
        !Object.keys(CardEnums.CARD_BRANDS).includes(data.src_initiator_id.toUpperCase().trim())
      ) {
        gtmData.api_key = data.src_initiator_id;
      }

      // For External SRC add "src_dpa_id" wherever we pass "apikey"
      if (gtmInstrumentationInstance && gtmInstrumentationInstance.getState().isExternalSrc) {
        gtmData.src_dpa_id = gtmInstrumentationInstance.getStore().src_dpa_id;
      }
    }
    // VCO-12817 - mapped INO apikey to MC matches, else it is a merchant API key
    // if srci_name is 'VISA', we're SRCi and can directly assign, else it is unknowable.
    gtmData.src_initiator_id = data.is_external_src
      ? srcInitiatorIdMap[data.src_initiator_id] || 'undefined'
      : CardEnums.CARD_BRANDS.VISA;
    gtmData.srci_name = data.srci_name;
    gtmData.src_system_name = data.src_system_name;
    gtmData.user_type = data.user_type;
    if (canUseSessionStorage() && sessionStorage.getItem('visitId')) {
      gtmData.xo_visitid = sessionStorage.getItem('visitId');
    } else if (data.xo_visitid) {
      gtmData.xo_visitid = data.xo_visitid;
    }
    if (data.event) {
      gtmData.event = data.event;
    }
    if (data.event_action) {
      gtmData.event_action = data.event_action;
    }
    if (data.event_label) {
      gtmData.event_label = data.event_label;
    }
    if (data.event_category) {
      gtmData.event_category = data.event_category;
    }
    if (data.otp_delivery_channel) {
      gtmData.otp_delivery_channel = data.otp_delivery_channel;
    }
    if (data.otp_validation_channel) {
      gtmData.otp_validation_channel = data.otp_validation_channel;
    }
    if (data.otp_provider) {
      gtmData.otp_provider = data.otp_provider;
    }
    if (data.experience_owner) {
      gtmData.experience_owner = data.experience_owner;
    }
  }
  _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmData)}`);
};

const trackTHMRequestEvent = data => {
  const ssiStatus = data.ssiStatus === 'OPTED_IN' ? 'SSI' : 'Non SSI';
  const gtmData = {
    event: THM_PROFILING,
    event_action: `${THM_PROFILING} Request`,
    event_label: `THM Request-Attempt-${data.thmProfilingAttempt}-${ssiStatus}`
  };

  _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmData)}`);
};

const trackTHMResponseEvent = data => {
  const profilingTime = Date.now() - data.profilingStartTime;
  const ssiStatus = data.ssiStatus === 'OPTED_IN' ? 'SSI' : 'Non SSI';
  const gtmData = {
    api_response: data.profilingStatus,
    elapsed_time: profilingTime,
    error_code: data.sessionId,
    event: THM_PROFILING,
    event_action: `${THM_PROFILING} Response`,
    event_label: `THM Response Received-${ssiStatus}`
  };

  _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmData)}`);
};

const trackCustomAttributes = data => {
  _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(data)}`);
};

class GtmInstrumentation {
  constructor() {
    if (!gtmInstrumentationInstance) {
      gtmInstrumentationInstance = this; // eslint-disable-line consistent-this

      this.__store = {};
      this.__state = {
        identityLookupFired: false,
        initFired: false,
        isExternalSrc: false,
        userTypeIdentificationFired: false,
        visaCardReturned: false,
        visaCardSelected: false
      };
    }

    return gtmInstrumentationInstance;
  }

  getState() {
    return this.__state;
  }

  getStore() {
    return this.__store;
  }

  trackInitInitiation(data = {}, method = '', APIStatus = 'Pending') {
    if (this.__state.initFired && APIStatus === 'Success') {
      return;
    }

    const srcInitiatorId = srcInitiatorIdMap[data.srcInitiatorId] || CardEnums.CARD_BRANDS.VISA;
    const _customData = data.customData || {};
    const _data = data.data || {};
    const _vinit = _data.vInitRequest || {};
    const _merchantConfig = _data.merchantConfig || {};

    let gtmEvent = {
      api_name: 'init',
      channel: 'merchant',
      event: 'Init initiation',
      event_action: 'Init initiation',
      event_category: 'Init initiation',
      event_label: `Init initiation-API ${method} ${APIStatus}`,
      experience_owner: 'SRCi',
      flow: 'INO'
    };

    if (data.srcInitiatorId && !data.visaSrci) {
      const srcInitiatorId = srcInitiatorIdMap[data.srcInitiatorId] || 'undefined';
      const dpaData = data.dpaData || {};
      const dpaTransactionOptions = data.dpaTransactionOptions || {};
      const customInputData = dpaTransactionOptions.customInputData || {};
      this.__state.isExternalSrc = true;
      this.__store = {
        apikey: data.srcInitiatorId,
        dpa_integration_type: customInputData.dpaIntegrationType || 'DEFAULT',
        dpa_presentation_name: dpaData.dpaPresentationName || 'undefined',
        dpa_uri: dpaData.dpaUri || 'undefined',
        merchant_country_code: dpaTransactionOptions.merchantCountryCode || 'undefined',
        src_dpa_id: dpaData.srcDpaId || 'undefined',
        src_initiator_id: srcInitiatorId,
        srci_dpa_id: data.srciDpaId || 'undefined',
        srci_transaction_id: data.srciTransactionId || 'undefined'
      };
    } else {
      this.__store = {
        api_key: _customData.apikey,
        browser_protocol: window.location.protocol.split(':')[0],
        correlation_id: _customData.correlationId,
        external_client_id: _customData.externalClientId || 'undefined',
        flow: _data.xoFlow,
        incoming_traffic_source: getIncomingTrafficSource(),
        merchant_country_code: _merchantConfig.merchantCountryCode,
        merchant_name: _vinit.displayName,
        merchant_site: _vinit.parentUrl,
        rememberme_type: cookieExist('ermEnabled') ? 'ERM' : 'legacy',
        src_initiator_id: srcInitiatorId,
        stay_signed_in: getSsiStatus(),
        user_type: getUserType(),
        username_remembered: getCookie('alwaysRemember') === 'true',
        xo_visitid: _data.visitId
      };
    }

    gtmEvent = {
      ...gtmEvent,
      ...this.__store
    };

    _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);
    if (APIStatus === 'Success') {
      this.__state.initFired = true;
    }
  }

  trackVsbInitialize(data = {}) {
    const srcInitiatorId = srcInitiatorIdMap[data.srcInitiatorId] || CardEnums.CARD_BRANDS.VISA;
    const _customData = data.customData || {};
    const _data = data.data || {};
    const _vinit = _data.vInitRequest || {};
    const _merchantConfig = _data.merchantConfig || {};

    let gtmEvent = {
      event: 'VSB_Initialize',
      event_action: 'VSB Initialize',
      event_category: 'VSB Initialize',
      event_label: `VSB Initialize-Request API Pending`
    };

    if (data.srcInitiatorId && !data.visaSrci) {
      const srcInitiatorId = srcInitiatorIdMap[data.srcInitiatorId] || 'undefined';
      const dpaData = data.dpaData || {};
      const dpaTransactionOptions = data.dpaTransactionOptions || {};
      const customInputData = dpaTransactionOptions.customInputData || {};
      this.__state.isExternalSrc = true;
      this.__store = {
        apikey: data.srcInitiatorId,
        dpa_integration_type: customInputData.dpaIntegrationType || 'DEFAULT',
        dpa_presentation_name: dpaData.dpaPresentationName || 'undefined',
        dpa_uri: dpaData.dpaUri || 'undefined',
        merchant_country_code: dpaTransactionOptions.merchantCountryCode || 'undefined',
        src_dpa_id: dpaData.srcDpaId || 'undefined',
        src_initiator_id: srcInitiatorId,
        srci_dpa_id: data.srciDpaId || 'undefined',
        srci_transaction_id: data.srciTransactionId || 'undefined'
      };
    } else {
      this.__store = {
        api_key: _customData.apikey,
        browser_protocol: window.location.protocol.split(':')[0],
        correlation_id: _customData.correlationId,
        external_client_id: _customData.externalClientId || 'undefined',
        flow: _data.xoFlow,
        incoming_traffic_source: getIncomingTrafficSource(),
        merchant_country_code: _merchantConfig.merchantCountryCode,
        merchant_integration_type: data.merchantIntegrationType || 'undefined',
        merchant_name: _vinit.displayName,
        merchant_site: _vinit.parentUrl,
        rememberme_type: cookieExist('ermEnabled') ? 'ERM' : 'legacy',
        src_initiator_id: srcInitiatorId,
        stay_signed_in: getSsiStatus(),
        time_to_load_vsb_sdk: data.vsbSDKLoadEnd || 'undefined',
        user_type: getUserType(),
        username_remembered: getCookie('alwaysRemember') === 'true',
        xo_visitid: _data.visitId
      };
    }

    gtmEvent = {
      ...gtmEvent,
      ...this.__store
    };

    _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);
  }

  trackCheckoutInitiation(data = {}, method = '', APIStatus = 'Pending') {
    const gtmEvent = {
      ...this.__store,
      api_name: 'checkout',
      card_flow: data.encryptedCard
        ? 'Enroll card'
        : data.srcDigitalCardId
          ? 'Select card'
          : 'undefined',
      channel: 'merchant',
      event: 'Checkout initiation',
      event_action: 'Checkout initiation',
      event_category: 'Checkout initiation',
      event_label: `Checkout initiation-${method} API ${APIStatus}`,
      experience_owner: 'SRCi'
    };

    _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);
  }
  trackNewUser() {
    if (this.__state.userTypeIdentificationFired) {
      return;
    }

    const gtmEvent = {
      ...this.__store,
      api_name: 'usernameLookup',
      auth_provider: CardEnums.CARD_BRANDS.VISA,
      channel: 'merchant',
      event: 'User type identification',
      event_action: 'User type identification',
      event_label: 'User type identification-API',
      experience_owner: 'DCF',
      nvr: false,
      src_system_name: CardEnums.CARD_BRANDS.VISA,
      user_type_identification: 'New user',
      vat: false,
      vr: false
    };

    _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);

    this.__store = {
      ...this.__store,
      auth_provider: CardEnums.CARD_BRANDS.VISA
    };

    this.__state.userTypeIdentificationFired = true;
  }

  trackCFEUserCheckout(userPresent) {
    /**
     * this should be fired under two circumstances:
     * 1. on calling usernameLookup after collecting email in DCF
     * 2. on calling checkout in external srci/psp flow and email is passed
     */
    if (this.__state.userTypeIdentificationFired) {
      return;
    }

    const userTypeIdentification = userPresent ? 'Unrecognized Returning user' : 'New user';
    const gtmEvent = {
      ...this.__store,
      api_name: 'checkout',
      auth_provider: CardEnums.CARD_BRANDS.VISA,
      channel: 'merchant',
      event: 'User type identification',
      event_action: 'User type identification',
      event_label: 'User type identification-API',
      experience_owner: 'DCF',
      nvr: false,
      src_system_name: CardEnums.CARD_BRANDS.VISA,
      user_type_identification: userTypeIdentification,
      vat: false,
      vr: false
    };

    _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);

    this.__store = {
      ...this.__store,
      auth_provider: CardEnums.CARD_BRANDS.VISA,
      user_type_identification: userTypeIdentification // ensure following events have updated user_type_identification
    };

    this.__state.userTypeIdentificationFired = true;
  }

  trackGuestUser() {
    const gtmEvent = {
      ...this.__store,
      api_name: 'initiateGuest',
      auth_provider: CardEnums.CARD_BRANDS.VISA,
      channel: 'merchant',
      event: 'User type identification',
      event_action: 'User type identification',
      event_label: 'User type identification-API',
      experience_owner: 'DCF',
      src_system_name: CardEnums.CARD_BRANDS.VISA,
      user_type_identification: 'Unrecognized Returning Guest user DCF'
    };

    _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);

    this.__store = {
      ...this.__store,
      auth_provider: CardEnums.CARD_BRANDS.VISA
    };

    this.__state.userTypeIdentificationFired = true;
  }

  trackUnrecognizedReturningUserDCF() {
    if (this.__state.userTypeIdentificationFired) {
      return;
    }

    const gtmEvent = {
      ...this.__store,
      api_name: 'usernameLookup',
      auth_provider: CardEnums.CARD_BRANDS.VISA,
      channel: 'merchant',
      event: 'User type identification',
      event_action: 'User type identification',
      event_label: 'User type identification-API',
      experience_owner: 'DCF',
      nvr: false,
      src_system_name: CardEnums.CARD_BRANDS.VISA,
      user_type_identification: 'Unrecognized Returning user DCF',
      vat: true,
      vr: false
    };

    _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);

    this.__store = {
      ...this.__store,
      auth_provider: CardEnums.CARD_BRANDS.VISA
    };

    this.__state.userTypeIdentificationFired = true;
  }

  trackIdentityLookup(data) {
    if (this.__state.isExternalSrc) {
      const gtmEvent = {
        ...this.__store,
        api_name: 'identityLookup',
        channel: 'merchant',
        event: 'Identity lookup',
        event_action: 'Identity lookup',
        event_label: 'Identity lookup-API',
        experience_owner: 'SRCi',
        src_system_name: CardEnums.CARD_BRANDS.VISA,
        user_type_identification: data?.consumerPresent ? 'Uncognized Returning user' : 'New User'
      };
      this.__store = {
        ...this.__store,
        user_type_identification: data?.consumerPresent ? 'Uncognized Returning user' : 'New User'
      };
      _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);

      this.__state.identityLookupFired = true;
    }
  }

  trackUsernameLookup(gtmData) {
    const { apiName = '', event, lookupStatus, saveMyInfoWithVisa, userFlow } = gtmData;

    const gtmEvent = {
      ...this.__store,
      api_name: apiName,
      channel: 'merchant',
      event,
      experience_owner: 'SRCi',
      save_my_info_with_visa: saveMyInfoWithVisa,
      user_flow: userFlow
    };

    /**
     * VCO-14406 - When /recognize api is enabled we will have already set `guest_vco_account` to
     * `PRESENT` or `NOTPRESENT` based on the /recognize response.  Therefore we should not be
     * resetting `guest_vco_account` to `undefined` when event === `user look up request` when
     * /recognize is enabled.  `user look up request` always passes `lookupStatus: 'undefined'`.
     */
    if (event === 'user look up response') {
      gtmEvent.guest_vco_account = lookupStatus;
    }

    _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);

    this.__store = {
      ...this.__store,
      user_flow: userFlow
    };
  }

  trackInitiateIdentityValidation(data = {}) {
    if (this.__state.isExternalSrc) {
      if (!this.__state.identityLookupFired) {
        return;
      }

      const maskedValidationChannel = (data.maskedValidationChannel || '').split(',');
      const otpDeliveryChannel =
        maskedValidationChannel.length > 1
          ? 'Email-SMS'
          : isEmail(maskedValidationChannel[0])
            ? 'Email'
            : 'SMS';
      const gtmEvent = {
        ...this.__store,
        api_name: 'initiateIdentityValidation',
        auth_provider: CardEnums.CARD_BRANDS.VISA,
        channel: 'merchant',
        event: 'Initiate identity validation',
        event_action: 'Initiate identity validation',
        event_label: 'Initiate identity validation-API',
        experience_owner: 'SRCi',
        otp_delivery_channel: otpDeliveryChannel,
        src_system_name: CardEnums.CARD_BRANDS.VISA
      };

      _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);
    }
  }

  trackCompleteIdentityValidation(data = {}) {
    if (this.__state.isExternalSrc) {
      if (!this.__state.identityLookupFired) {
        return;
      }

      const apiStatus = data.error && !isEmptyObject(data.error) ? 'Failed' : 'Successful';
      const gtmEvent = {
        ...this.__store,
        api_name: 'completeIdentityValidation',
        auth_provider: CardEnums.CARD_BRANDS.VISA,
        channel: 'merchant',
        event: 'Identity validation reponse',
        event_action: 'Identity validation reponse',
        event_label: `Identity validation reponse-API: ${apiStatus}`,
        experience_owner: 'SRCi',
        src_system_name: CardEnums.CARD_BRANDS.VISA
      };

      _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);
    }
  }

  trackStepUp(method, type = '') {
    const status =
      method === 'Request' ? 'pending' : type.includes('complete') ? 'Success' : 'Error';
    const gtmEvent = {
      ...this.__store,
      api_name: 'oauth2/token/2fa',
      auth_provider: CardEnums.CARD_BRANDS.VISA,
      channel: 'merchant',
      event: 'Step up user',
      event_action: 'Step up user',
      event_label: `Step up user-API ${method} ${status}`,
      experience_owner: 'DCF',
      src_system_name: CardEnums.CARD_BRANDS.VISA
    };

    _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);
  }

  trackCardRequested() {
    const gtmEvent = {
      ...this.__store,
      api_name: 'getSrcProfile',
      channel: 'merchant',
      event: 'Card requested',
      event_action: 'Card requested',
      event_label: 'Card requested-API',
      experience_owner: 'SRCi',
      src_system_name: CardEnums.CARD_BRANDS.VISA
    };

    _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);
  }

  trackCardReturned() {
    const gtmEvent = {
      ...this.__store,
      api_name: 'getSrcProfile',
      channel: 'merchant',
      event: 'Card returned',
      event_action: 'Card returned',
      event_label: `Card returned-API`,
      src_system_name: CardEnums.CARD_BRANDS.VISA
    };

    _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);
  }

  __getCardCount(data = []) {
    return data.reduce(
      (accum, obj) => {
        if (
          obj.networkId === CardEnums.CARD_BRANDS.VISA ||
          obj.network === CardEnums.CARD_BRANDS.VISA ||
          this.__state.isExternalSrc
        ) {
          this.__state.visaCardReturned = true;
          accum[CardEnums.CARD_BRANDS.VISA] = ++accum[CardEnums.CARD_BRANDS.VISA];
        }

        if (
          obj.networkId === CardEnums.CARD_BRANDS.MASTERCARD ||
          obj.networkId === CardEnums.CARD_BRANDS.MA ||
          obj.network === CardEnums.CARD_BRANDS.MASTERCARD ||
          obj.network === CardEnums.CARD_BRANDS.MA
        ) {
          accum[CardEnums.CARD_BRANDS.MASTERCARD] = ++accum[CardEnums.CARD_BRANDS.MASTERCARD];
        }

        if (
          obj.networkId === CardEnums.CARD_BRANDS.AMEX ||
          obj.network === CardEnums.CARD_BRANDS.AMEX
        ) {
          accum[CardEnums.CARD_BRANDS.AMEX] = ++accum[CardEnums.CARD_BRANDS.AMEX];
        }

        return accum;
      },
      {
        [CardEnums.CARD_BRANDS.VISA]: 0,
        [CardEnums.CARD_BRANDS.MASTERCARD]: 0,
        [CardEnums.CARD_BRANDS.AMEX]: 0
      }
    );
  }

  trackCardPresent(data = []) {
    if (Array.isArray(data) && data.length > 0) {
      const cardBrandCount = this.__getCardCount(data);

      for (const brand in cardBrandCount) {
        if (cardBrandCount[brand] > 0) {
          const gtmEvent = {
            ...this.__store,
            api_name: 'getSrcProfile',
            channel: 'merchant',
            event: 'Card presented',
            event_action: `${brand} Card Presented`,
            event_label: `${brand} Card Presented -  ${cardBrandCount[brand]}`,
            src_system_name: CardEnums.CARD_BRANDS.VISA
          };

          _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);
        }
      }
    }
  }

  trackGuestVcoAccountEvent() {
    const gtmEvent = {
      guest_vco_account: 'PRESENT',
      user_flow: 'existing'
    };
    _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);
  }

  trackVisaCardSelected(data = {}) {
    if (!this.__state.visaCardReturned) {
      return;
    }

    this.__state.visaCardSelected = data.visaCardSelected || this.__state.isExternalSrc;

    if (this.__state.visaCardSelected) {
      const gtmEvent = {
        ...this.__store,
        api_name: 'checkout',
        channel: 'merchant',
        event: 'Card selected',
        event_action: 'Card selected',
        event_label: `Card selected-API: ${data.cardType || 'undefined'}`,
        src_system_name: CardEnums.CARD_BRANDS.VISA
      };

      _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmEvent)}`);
    }
  }

  resetApiStateFlags() {
    this.__state = {
      identityLookupFired: false,
      initFired: false,
      isExternalSrc: false,
      userTypeIdentificationFired: false,
      visaCardReturned: false,
      visaCardSelected: false
    };
  }

  updateStore(data = {}) {
    this.__store = {
      ...this.__store,
      ...data
    };
  }
}

export const GtmInstr = new GtmInstrumentation();

export const uuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const GtmHelper = {
  trackCustomAttributes,
  trackINOEvent,
  trackTHMRequestEvent,
  trackTHMResponseEvent,
  uuid
};

export default GtmHelper;
